import { useVisualElementsCounts } from 'hooks/queries/visualElements/useVisualElementsCounts'
import Styles from '../TemplatesV2.module.scss'
import { forwardRef } from 'react'
import { PressProps, useHover } from '@react-aria/interactions'
import { useObjectRef } from 'utils/useObjectRef'
import { useButton } from '@react-aria/button'
import { mergeProps } from '@react-aria/utils'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { SelectableItem } from 'components/Menu/MenuItem'
import { TEMPLATE_STYLES, TEMPLATE_TYPES } from 'components/Wizards/Wizard/Form/TemplateSelect/constants'
import { Selection } from '@react-types/shared'
import { ChevronDown2, Close } from 'blaze-ui/icons'
import { startCase } from 'lodash'
import cx from 'classnames'
import { FilterKey, IFilters } from '../Types'
import { ASPECT_RATIOS, AspectRatioKey } from 'components/NewFileModal/Types'
import { useGroups } from 'components/Wizards/Catalogue/useGroups'
import { TextChannelKey, TurnInto } from 'types'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { Section } from '@react-stately/collections'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { Carousel } from 'components/Carousel/Carousel'
import { Button } from 'blaze-ui/Button'

interface Props {
  value: IFilters
  onChange: (f: IFilters) => any
  useCompactUI?: boolean
  filterButtonVariant?: 'primary' | 'secondary'
  hiddenFilters?: FilterKey[]
  disabledFilters?: FilterKey[]
  carouselOffset?: number
}

interface FilterProps extends Pick<Props, 'useCompactUI' | 'filterButtonVariant'> {
  unselectedLabel: string
  items: Record<string, string>
  selectedKeys: Selection
  isDisabled: boolean
  onChange: (f: 'all' | string) => any
}

interface FilterButtonProps extends PressProps, Pick<Props, 'useCompactUI' | 'filterButtonVariant'> {
  hasSelections: boolean
  label: string
  isClearable?: boolean
  alwaysShowChevron?: boolean
}

interface FormatFilterProps extends Pick<Props, 'useCompactUI' | 'filterButtonVariant'> {
  aspectRatioKey?: AspectRatioKey
  isDisabled: boolean
  onChange: (key: AspectRatioKey) => any
}

const getLabel = (selectedKeys: Selection, unselectedLabel: string) => {
  if (selectedKeys === 'all') {
    return unselectedLabel
  }

  const selectedKeysArray = [...selectedKeys] as string[]

  if (selectedKeysArray.length > 1) {
    return `${startCase(selectedKeysArray[0])} + ${selectedKeysArray.length - 1}`
  }

  return startCase(`${selectedKeysArray[0]}`)
}

const FilterButton = forwardRef<HTMLDivElement, FilterButtonProps>(
  (
    {
      hasSelections,
      label,
      isClearable = true,
      useCompactUI,
      filterButtonVariant = 'primary',
      alwaysShowChevron = false,
      ...props
    },
    fRef,
  ) => {
    const ref = useObjectRef(fRef)
    const { buttonProps } = useButton({ ...props, elementType: 'div' }, ref)
    const { hoverProps, isHovered } = useHover(props)
    const displayChevron = alwaysShowChevron || !hasSelections

    return (
      <div
        ref={ref}
        {...mergeProps(buttonProps, hoverProps)}
        className={cx(Styles[filterButtonVariant], Styles.filterButton, {
          [Styles.compact]: useCompactUI,
          [Styles.selected]: hasSelections,
          [Styles.clearable]: isClearable,
          [Styles.hovered]: isHovered,
          [Styles.disabled]: props.isDisabled,
          [Styles.noIcon]: !displayChevron,
        })}
      >
        {label}
        {displayChevron && <ChevronDown2 />}
      </div>
    )
  },
)

const Filter = ({
  unselectedLabel,
  items,
  selectedKeys,
  onChange,
  useCompactUI,
  filterButtonVariant,
  isDisabled,
}: FilterProps) => {
  return (
    <div className={Styles.filterContainer}>
      <MenuTrigger>
        <FilterButton
          hasSelections={selectedKeys !== 'all'}
          label={getLabel(selectedKeys, unselectedLabel)}
          isDisabled={isDisabled || Object.keys(items).length === 0}
          isClearable={!isDisabled}
          useCompactUI={useCompactUI}
          filterButtonVariant={filterButtonVariant}
        />
        <Menu
          selectionMode="multiple"
          selectedKeys={selectedKeys === 'all' ? [] : selectedKeys}
          onAction={(key) => onChange(key as string)}
          disabledKeys={selectedKeys === 'all' ? ['all'] : []}
        >
          {useCompactUI && (
            <SelectableItem closeOnSelect={true} type="item" key="all">
              Clear {unselectedLabel} Filters
            </SelectableItem>
          )}
          {Object.entries(items).map(([key, label]) => (
            <SelectableItem closeOnSelect={false} type="checkbox" key={key}>
              {label}
            </SelectableItem>
          ))}
        </Menu>
      </MenuTrigger>
      {selectedKeys !== 'all' && !isDisabled && (
        <button
          aria-label={`Clear ${unselectedLabel}`}
          className={Styles.clearButtonContainer}
          onClick={() => onChange('all')}
        >
          <Close size={14} />
        </button>
      )}
    </div>
  )
}

const FormatFilter = ({
  aspectRatioKey,
  onChange,
  isDisabled,
  useCompactUI,
  filterButtonVariant,
}: FormatFilterProps) => {
  const groups = useGroups(
    new Set<TextChannelKey | AspectRatioKey>([
      ...Object.values(TurnInto),
      ...Object.values(ChannelType),
      GenerationType.Campaign,
      GenerationType.Brainstorm,
      GenerationType.Outline,
      GenerationType.ContentBrief,
      GenerationType.SEOPlanning,
      GenerationType.TwitterPlanning,
      GenerationType.InstagramPlanning,
      GenerationType.FacebookPlanning,
      GenerationType.LinkedInPlanning,
    ]),
  ).filter((group) => group.name !== 'Popular' && group.name !== 'Tools')

  if (isDisabled && aspectRatioKey) {
    return (
      <FilterButton
        hasSelections
        label={ASPECT_RATIOS[aspectRatioKey].name}
        isDisabled
        isClearable={false}
        useCompactUI={useCompactUI}
        filterButtonVariant={filterButtonVariant}
      />
    )
  }

  return (
    <div className={Styles.filterContainer}>
      <MenuTrigger>
        <FilterButton
          hasSelections={!!aspectRatioKey}
          label={aspectRatioKey ? ASPECT_RATIOS[aspectRatioKey].name : 'Format'}
          isClearable={!useCompactUI}
          useCompactUI={useCompactUI}
          filterButtonVariant={filterButtonVariant}
          alwaysShowChevron={useCompactUI}
        />
        <Menu
          selectionMode="single"
          selectedKeys={aspectRatioKey ? [aspectRatioKey] : []}
          onAction={(key) => onChange(key as AspectRatioKey)}
        >
          {groups.map((group) => (
            <Section key={group.name} title={group.name} items={group.channels}>
              {group.channels.map((channel) => {
                const { name, icon: Icon, width, height } = ASPECT_RATIOS[channel as AspectRatioKey]
                const isSelected = channel === aspectRatioKey

                return (
                  <SelectableItem
                    type="item"
                    key={channel}
                    textValue={name}
                    frontIcon={() => <Icon size={20} color={isSelected ? '#FFFFFF' : undefined} />}
                  >
                    {name}
                    <span className={cx(Styles.formatDimensions, { [Styles.selected]: isSelected })}>
                      {width} x {height}
                    </span>
                  </SelectableItem>
                )
              })}
            </Section>
          ))}
        </Menu>
      </MenuTrigger>
      {aspectRatioKey && !useCompactUI && (
        <button
          aria-label="Clear format"
          className={Styles.clearButtonContainer}
          onClick={() => onChange(aspectRatioKey)}
        >
          <Close size={14} />
        </button>
      )}
    </div>
  )
}

export const Filters = ({
  value,
  onChange,
  useCompactUI = false,
  filterButtonVariant = 'primary',
  hiddenFilters = [],
  disabledFilters = [],
  carouselOffset = 15,
}: Props) => {
  const { types, styles, categories, aspectRatioKey } = value
  const { data } = useVisualElementsCounts({
    element_type: 'Template',
    type: types === 'all' ? undefined : ([...types] as string[]),
    style: styles === 'all' ? undefined : ([...styles] as string[]),
    width: aspectRatioKey ? ASPECT_RATIOS[aspectRatioKey].width : undefined,
    height: aspectRatioKey ? ASPECT_RATIOS[aspectRatioKey].height : undefined,
    ...Object.values(value),
  })
  const populatedSortedCategories = data
    ? Object.fromEntries(
        Object.keys(data)
          .sort()
          .map((key) => [key, key]),
      )
    : {}

  const handleFilterChange = (filterType: 'categories' | 'types' | 'styles', filter: 'all' | string) => {
    if (value[filterType] === 'all') {
      return onChange({ ...value, [filterType]: [filter] })
    }

    if (filter === 'all') {
      return onChange({ ...value, [filterType]: 'all' })
    }

    const selectedFilters = new Set(value[filterType])

    if (selectedFilters.has(filter)) {
      selectedFilters.delete(filter)
    } else {
      selectedFilters.add(filter)
    }

    if (selectedFilters.size < 1) {
      return onChange({ ...value, [filterType]: 'all' })
    }

    onChange({ ...value, [filterType]: selectedFilters })
  }

  const handleFormatChange = (newAspectRatioKey: AspectRatioKey) => {
    if (newAspectRatioKey === aspectRatioKey && !useCompactUI) {
      onChange({ ...value, aspectRatioKey: undefined })
      return
    }

    if (newAspectRatioKey === aspectRatioKey && useCompactUI) {
      return
    }

    onChange({ ...value, aspectRatioKey: newAspectRatioKey })
  }

  const filterProperties = [
    {
      key: 'types',
      unselectedLabel: 'Type',
      items: TEMPLATE_TYPES,
      selectedKeys: types,
      onChange: (filter: string) => handleFilterChange('types', filter),
    },
    {
      key: 'categories',
      unselectedLabel: 'Category',
      items: populatedSortedCategories,
      selectedKeys: categories,
      onChange: (filter: string) => handleFilterChange('categories', filter),
    },
    {
      key: 'styles',
      unselectedLabel: 'Style',
      items: TEMPLATE_STYLES,
      selectedKeys: styles,
      onChange: (filter: string) => handleFilterChange('styles', filter),
    },
  ]

  const filterPropertiesSortedBySelected = [...filterProperties].sort(
    (a, b) => Number(b.selectedKeys !== 'all') - Number(a.selectedKeys !== 'all'),
  )

  return (
    <div className={cx(Styles.filtersContainer, { [Styles.compact]: useCompactUI })}>
      <ConditionalWrapper
        condition={useCompactUI}
        wrapper={(children) => (
          <Carousel slideGap={5} buttonSize="xs" offset={carouselOffset}>
            {children}
          </Carousel>
        )}
      >
        {!hiddenFilters.includes('format') && (
          <FormatFilter
            aspectRatioKey={aspectRatioKey}
            onChange={handleFormatChange}
            isDisabled={disabledFilters.includes('format')}
            useCompactUI={useCompactUI}
            filterButtonVariant={filterButtonVariant}
          />
        )}
        {(useCompactUI ? filterPropertiesSortedBySelected : filterProperties)
          .filter((filterProperties) => !hiddenFilters.includes(filterProperties.key as FilterKey))
          .map((filterProperties) => (
            <Filter
              {...filterProperties}
              key={filterProperties.key}
              useCompactUI={useCompactUI}
              filterButtonVariant={filterButtonVariant}
              isDisabled={disabledFilters.includes(filterProperties.key as FilterKey)}
            />
          ))}
        {useCompactUI && (types !== 'all' || categories !== 'all' || styles !== 'all') && (
          <Button
            onPress={() => onChange({ ...value, types: 'all', categories: 'all', styles: 'all' })}
            variant="ghost"
            size="sm"
          >
            Clear All Filters
          </Button>
        )}
      </ConditionalWrapper>
    </div>
  )
}
